import React, { FC } from 'react';
import {
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  Box,
  VStack,
  HStack,
  Button,
  Center
} from '@chakra-ui/react';
import { PairToken } from '@app/types/pool';
import { LabeledValue } from '@app/components/LabeledValue';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { Icon } from '@app/components/Icon';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { TransactionError } from '@thirdweb-dev/react';
import { SubmitButton } from '@app/components/SubmitButton';
import { NetworkMismatchButton } from '@app/components/NetworkMismatchButton';
import { useSplitPosition } from '@app/screens/positions/components/SpNftControls/SplitPosition/hooks';
import { KpNFTControlsHeader } from '@app/screens/positions/components/SpNftControls/KpNFTControlsHeader';
import { Vault } from '@app/hooks/plugins/useVaults';
import { formatNumber } from '@app/helpers/format';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tokenDetails: PairToken | Vault;
  tokenId: string;
  deposit: string;
  poolAddress: string;
  onResult: (res: {
    status: 'success' | 'error';
    title: string;
    message: string;
    txHash?: string;
  }) => void;
}

export const SplitPositionModal: FC<Props> = ({
  isOpen,
  onClose,
  tokenDetails,
  tokenId,
  deposit,
  poolAddress,
  onResult
}) => {
  const isMismatchedNetwork = useNetworkMismatch();
  const isLpToken = 'lpToken' in tokenDetails;

  const { state, handleAmountChange, resetStateValues, submit, submitting } =
    useSplitPosition(tokenDetails, poolAddress, tokenId);

  const isBalanceExceeded = deposit !== undefined && +deposit < +state.amount;

  function renderSubmit() {
    if (isMismatchedNetwork) {
      return <NetworkMismatchButton />;
    }

    return (
      <SubmitButton
        mt={0}
        disabled={isBalanceExceeded || !+state.amount}
        onClick={async () => {
          try {
            const res = await submit();

            if (res['reason'] !== undefined) {
              showErrorToast(res.reason);
            } else if (res instanceof Error) {
              showErrorToast(res.message);
            } else {
              resetStateValues();

              const txHash = res?.receipt?.transactionHash;

              showSuccessToast(
                `Transaction Confirmed`,
                <ExternalLink txHash={txHash} />
              );

              onResult({
                status: 'success',
                title: 'Transaction Confirmed',
                message: 'Your transaction has been successfully completed',
                txHash
              });
            }
          } catch (e) {
            if (e instanceof TransactionError) {
              showErrorToast(e.reason);
            } else {
              showErrorToast(`Transaction error`);
            }

            onResult({
              status: 'error',
              title: 'Transaction Error',
              message:
                'There was an error processing your transaction. Please check the details and try again'
            });
          }
        }}
        isLoading={submitting}
        label="Split"
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      isCentered
      motionPreset="slideInBottom"
      size="xs"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '586px'], padding: '0px' }}>
        <Box p="24px">
          <KpNFTControlsHeader
            tokenDetails={tokenDetails}
            tokenId={tokenId}
            title="Split your position"
            subtitle="Break your spNFT into two smaller positions"
          />
          <HStack justifyContent="space-between" mt={6}>
            <Text fontSize="16px" fontWeight={400} lineHeight="26px">
              Amount to split
            </Text>

            {isLpToken ? (
              <LabeledValue
                label={`Splittable amount ${tokenDetails.lpToken.symbol}:`}
                isLoading={false}
                value={formatNumber(+deposit)}
              />
            ) : (
              <LabeledValue
                label={`Splittable amount ${tokenDetails.symbol}:`}
                isLoading={false}
                value={formatNumber(+deposit)}
              />
            )}
          </HStack>
          <HStack
            justifyContent="space-between"
            mt="18px"
            sx={{
              paddingRight: '14px',
              borderRadius: '10px',
              border: '1px solid',
              borderColor: 'gray.5',
              background: 'black'
            }}
          >
            <DebouncedInput
              amount={state.amount}
              onChange={handleAmountChange}
              textAlign="left"
            />
            <Button
              variant="tertiary"
              onClick={() => {
                if (deposit === undefined) {
                  return;
                }

                handleAmountChange(deposit.toString());
              }}
              sx={{
                color: 'orange',
                bg: 'gray.2',
                width: 'fit-content',
                fontSize: '14px',
                padding: '14px 28px'
              }}
            >
              MAX
            </Button>
          </HStack>
          <HStack justifyContent="space-between" mt={6}>
            <Text fontSize="16px" fontWeight={400} lineHeight="26px">
              Projected result
            </Text>
          </HStack>
          <HStack>
            <Center
              sx={{
                flex: 1,
                border: '1px solid',
                borderColor: 'neutral.800',
                background: 'neutral.1000',
                padding: '12px',
                borderRadius: '12px'
              }}
            >
              {formatNumber(+deposit)}{' '}
              {isLpToken ? tokenDetails.lpToken.symbol : tokenDetails.symbol}
            </Center>
            <Icon
              name="arrowRight"
              style={{ color: 'var(--chakra-colors-orange)' }}
            />
            <VStack sx={{ flex: 1, width: '100%' }}>
              <Center
                sx={{
                  flex: 1,
                  width: '100%',
                  border: '1px solid',
                  borderColor: 'neutral.800',
                  background: 'neutral.1000',
                  padding: '12px',
                  borderRadius: '12px'
                }}
              >
                {formatNumber(+state.amount || 0)}{' '}
                {isLpToken ? tokenDetails.lpToken.symbol : tokenDetails.symbol}
              </Center>
              <Center
                sx={{
                  flex: 1,
                  width: '100%',
                  border: '1px solid',
                  borderColor: 'neutral.800',
                  background: 'neutral.1000',
                  padding: '12px',
                  borderRadius: '12px'
                }}
              >
                {formatNumber(+deposit - +state.amount)}{' '}
                {isLpToken ? tokenDetails.lpToken.symbol : tokenDetails.symbol}
              </Center>
            </VStack>
          </HStack>
          <HStack mt={6}>
            <Button
              variant="secondary"
              onClick={() => {
                resetStateValues();
                onClose();
              }}
              w="100%"
            >
              Cancel
            </Button>
            {renderSubmit()}
          </HStack>
        </Box>
      </ModalContent>
    </Modal>
  );
};
